import { useEffect, useState, useMemo } from 'react';
import { AlertCircle, CheckCircle2, FileSpreadsheet, Upload, X } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { trpc } from '@/lib/trpc';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  eventoId: string;
  onCommitted: () => void;
}

type Step = 'upload' | 'parsing' | 'preview' | 'result';

interface ParsedItem {
  nome: string;
  clube?: string | null;
  restricaoAlimentar?: string | null;
  lote?: string | null;
  valor?: string | null;
}

interface ResumidaRow {
  clube: string;
  lote: string;
  qtd: number;
  valorTotal: number;
}

const HEADERS_OBRIGATORIOS = ['Clube', 'Pessoa', 'Lote', 'Valor'];
const RESUMIDA_HEADERS_OBRIGATORIOS = ['Clube', 'Lote', 'Qtd', 'Valor Total'];

function findHeaderRow(
  XLSX: typeof import('xlsx'),
  ws: import('xlsx').WorkSheet,
  required: string[],
  maxRows = 15,
): number {
  const ref = ws['!ref'];
  if (!ref) return -1;
  const range = XLSX.utils.decode_range(ref);
  const limit = Math.min(maxRows, range.e.r + 1);
  for (let r = 0; r < limit; r++) {
    const valuesInRow: string[] = [];
    for (let c = range.s.c; c <= range.e.c; c++) {
      const cell = ws[XLSX.utils.encode_cell({ r, c })];
      if (cell && cell.v !== null && cell.v !== undefined && cell.v !== '') {
        valuesInRow.push(String(cell.v).trim());
      }
    }
    const allFound = required.every((req) => valuesInRow.some((v) => v.toLowerCase() === req.toLowerCase()));
    if (allFound) return r;
  }
  return -1;
}

function parseValor(raw: unknown): string | null {
  if (raw === null || raw === undefined || raw === '') return null;
  if (typeof raw === 'number') return raw.toFixed(2);
  const s = String(raw)
    .replace(/R\$\s*/gi, '')
    .replace(/\./g, '')
    .replace(',', '.')
    .trim();
  const n = Number(s);
  if (Number.isNaN(n)) return null;
  return n.toFixed(2);
}

function cleanText(raw: unknown): string | null {
  if (raw === null || raw === undefined) return null;
  const s = String(raw).replace(/ /g, ' ').replace(/\s+/g, ' ').trim();
  return s.length === 0 ? null : s;
}

function cleanRestricao(raw: unknown): string | null {
  const s = cleanText(raw);
  if (!s) return null;
  const lower = s.toLowerCase();
  if (lower === '.' || lower === 'não' || lower === 'nao' || lower === 'nenhuma') return null;
  return s;
}

function formatCurrency(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '-';
  const n = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(n)) return '-';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(n);
}

const DIFF_LABELS: Record<string, string> = {
  clube: 'Clube',
  restricaoAlimentar: 'Restrição',
  lote: 'Lote',
  valor: 'Valor',
};

export function ImportInscricoesModal({ isOpen, onClose, eventoId, onCommitted }: Props) {
  const [step, setStep] = useState<Step>('upload');
  const [detalhadoFile, setDetalhadoFile] = useState<File | null>(null);
  const [resumidaFile, setResumidaFile] = useState<File | null>(null);
  const [statusPadrao, setStatusPadrao] = useState<'PAGO' | 'PENDENTE'>('PAGO');
  const [parsedItems, setParsedItems] = useState<ParsedItem[]>([]);
  const [resumida, setResumida] = useState<ResumidaRow[]>([]);
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [parseInfo, setParseInfo] = useState<string>('');
  const [committing, setCommitting] = useState(false);
  const [commitResult, setCommitResult] = useState<{ criadas: number; atualizadas: number; ignoradas: number; removidas: number } | null>(null);

  const [selecaoNovas, setSelecaoNovas] = useState<Set<number>>(new Set());
  const [selecaoAtualizacoes, setSelecaoAtualizacoes] = useState<Set<number>>(new Set());
  const [selecaoAmbiguas, setSelecaoAmbiguas] = useState<Set<number>>(new Set());
  const [selecaoAusentes, setSelecaoAusentes] = useState<Set<number>>(new Set());
  const [activeTab, setActiveTab] = useState<'novas' | 'atualizacoes' | 'inalteradas' | 'ambiguas' | 'ausentes'>('novas');

  const previewMutation = trpc.eventos.previewImportInscricoes.useMutation();
  const commitMutation = trpc.eventos.commitImportInscricoes.useMutation();

  useEffect(() => {
    if (step === 'preview' && parsedItems.length > 0 && !previewMutation.data && !previewMutation.isPending) {
      previewMutation.mutate({ eventoId, items: parsedItems });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, parsedItems]);

  const handleReset = () => {
    setStep('upload');
    setDetalhadoFile(null);
    setResumidaFile(null);
    setParsedItems([]);
    setResumida([]);
    setParseErrors([]);
    setParseInfo('');
    setCommitResult(null);
    setSelecaoNovas(new Set());
    setSelecaoAtualizacoes(new Set());
    setSelecaoAmbiguas(new Set());
    setSelecaoAusentes(new Set());
    setActiveTab('novas');
    setCommitting(false);
    previewMutation.reset();
    commitMutation.reset();
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const handleAnalisar = async () => {
    if (!detalhadoFile) return;
    setStep('parsing');
    setParseErrors([]);
    previewMutation.reset();

    try {
      const XLSX = await import('xlsx');
      const buf = await detalhadoFile.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]!]!;

      const headerRow = findHeaderRow(XLSX, ws, ['Clube', 'Pessoa']);
      if (headerRow === -1) {
        setParseErrors([
          'Não foi possível localizar a linha de cabeçalho.',
          'Esperado encontrar colunas "Clube" e "Pessoa" em alguma das primeiras 15 linhas.',
        ]);
        setStep('upload');
        return;
      }
      const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(ws, { range: headerRow, defval: null });

      if (rows.length === 0) {
        setParseErrors(['Planilha vazia ou sem dados na linha 4 em diante.']);
        setStep('upload');
        return;
      }

      const firstRowKeys = Object.keys(rows[0]!);
      const missing = HEADERS_OBRIGATORIOS.filter((h) => !firstRowKeys.includes(h));
      if (missing.length > 0) {
        setParseErrors([
          `Headers obrigatórios não encontrados: ${missing.join(', ')}.`,
          'Esta não parece ser a planilha detalhada do Even3. Headers esperados na linha 3: Clube, Pessoa, Restrição, Lote, Valor.',
        ]);
        setStep('upload');
        return;
      }

      const items: ParsedItem[] = [];
      const errors: string[] = [];
      let descartadas = 0;

      rows.forEach((row, idx) => {
        const linha = idx + headerRow + 2;
        const nome = cleanText(row['Pessoa']);
        if (!nome) {
          descartadas++;
          return;
        }
        const valorRaw = row['Valor'];
        const valor = valorRaw === null || valorRaw === undefined || valorRaw === '' ? null : parseValor(valorRaw);
        if (valorRaw !== null && valorRaw !== undefined && valorRaw !== '' && valor === null) {
          errors.push(`Linha ${linha}: valor inválido "${String(valorRaw)}"`);
          return;
        }
        items.push({
          nome,
          clube: cleanText(row['Clube']),
          restricaoAlimentar: cleanRestricao(row['Restrição']),
          lote: cleanText(row['Lote']),
          valor,
        });
      });

      // Parse resumida se enviada
      if (resumidaFile) {
        try {
          const rbuf = await resumidaFile.arrayBuffer();
          const rwb = XLSX.read(rbuf, { type: 'array' });
          const rws = rwb.Sheets[rwb.SheetNames[0]!]!;
          const rHeaderRow = findHeaderRow(XLSX, rws, ['Clube', 'Lote', 'Qtd']);
          const rrows = rHeaderRow === -1
            ? []
            : XLSX.utils.sheet_to_json<Record<string, unknown>>(rws, { range: rHeaderRow, defval: null });
          const rkeys = rrows[0] ? Object.keys(rrows[0]) : [];
          const rmissing = RESUMIDA_HEADERS_OBRIGATORIOS.filter((h) => !rkeys.includes(h));
          if (rmissing.length === 0) {
            const parsed: ResumidaRow[] = [];
            rrows.forEach((r) => {
              const clube = cleanText(r['Clube']);
              const lote = cleanText(r['Lote']);
              const qtd = Number(r['Qtd']);
              const valorTotal = Number(parseValor(r['Valor Total']) ?? 0);
              if (clube && lote && !Number.isNaN(qtd)) {
                parsed.push({ clube, lote, qtd, valorTotal });
              }
            });
            setResumida(parsed);
          } else {
            errors.push(`Planilha resumida ignorada: headers faltando (${rmissing.join(', ')})`);
          }
        } catch (err) {
          errors.push(`Erro ao ler planilha resumida: ${(err as Error).message}`);
        }
      }

      setParsedItems(items);
      setParseInfo(
        `${items.length} linha${items.length === 1 ? '' : 's'} válida${items.length === 1 ? '' : 's'}` +
          (descartadas > 0 ? `, ${descartadas} descartada${descartadas === 1 ? '' : 's'} (sem nome)` : ''),
      );

      if (errors.length > 0) {
        setParseErrors(errors);
        setStep('upload');
        return;
      }

      if (items.length === 0) {
        setParseErrors(['Nenhuma linha válida encontrada.']);
        setStep('upload');
        return;
      }

      // Inicializa seleções (default: tudo selecionado em novas e atualizações)
      setSelecaoNovas(new Set());
      setSelecaoAtualizacoes(new Set());
      setStep('preview');
    } catch (err) {
      setParseErrors([`Erro ao processar planilha: ${(err as Error).message}`]);
      setStep('upload');
    }
  };

  // Quando preview chega, marca tudo de novas e atualizações por default
  useMemo(() => {
    if (previewMutation.data) {
      setSelecaoNovas(new Set(previewMutation.data.novas.map((_, i) => i)));
      setSelecaoAtualizacoes(new Set(previewMutation.data.atualizacoes.map((_, i) => i)));
      setSelecaoAmbiguas(new Set());
      setSelecaoAusentes(new Set());
    }
  }, [previewMutation.data]);

  const handleCommit = async () => {
    if (!previewMutation.data) return;
    setCommitting(true);

    // Combina novas selecionadas com ambíguas selecionadas (estas serão criadas como novas)
    const criar = [
      ...previewMutation.data.novas.filter((_, i) => selecaoNovas.has(i)),
      ...previewMutation.data.ambiguas.filter((_, i) => selecaoAmbiguas.has(i)),
    ];
    const atualizar = previewMutation.data.atualizacoes
      .filter((_, i) => selecaoAtualizacoes.has(i))
      .map((a) => ({
        id: a.existingId,
        clube: a.novo.clube ?? null,
        restricaoAlimentar: a.novo.restricaoAlimentar ?? null,
        lote: a.novo.lote ?? null,
        valor: a.novo.valor ?? null,
      }));

    const remover = previewMutation.data.ausentes
      .filter((_, i) => selecaoAusentes.has(i))
      .map((a) => a.id);

    try {
      const result = await commitMutation.mutateAsync({
        eventoId,
        statusPagamentoPadrao: statusPadrao,
        criar,
        atualizar,
        remover,
      });
      setCommitResult(result);
      setStep('result');
      onCommitted();
    } catch (err) {
      alert(`Erro ao importar: ${(err as Error).message}`);
      setCommitting(false);
    }
  };

  const reconciliacao = useMemo(() => {
    if (resumida.length === 0 || !commitResult) return null;
    // Group parsed items by (clube + lote) — uses normalized comparison
    const importadoMap = new Map<string, { qtd: number; valorTotal: number }>();
    for (const item of parsedItems) {
      const key = `${(item.clube ?? '').toLowerCase().trim()}|${(item.lote ?? '').toLowerCase().trim()}`;
      const cur = importadoMap.get(key) ?? { qtd: 0, valorTotal: 0 };
      cur.qtd += 1;
      cur.valorTotal += Number(item.valor ?? 0);
      importadoMap.set(key, cur);
    }
    return resumida.map((r) => {
      const key = `${r.clube.toLowerCase().trim()}|${r.lote.toLowerCase().trim()}`;
      const imp = importadoMap.get(key) ?? { qtd: 0, valorTotal: 0 };
      const diffQtd = imp.qtd - r.qtd;
      const diffValor = imp.valorTotal - r.valorTotal;
      return {
        clube: r.clube,
        lote: r.lote,
        qtdDeclarada: r.qtd,
        qtdImportada: imp.qtd,
        valorDeclarado: r.valorTotal,
        valorImportado: imp.valorTotal,
        diffQtd,
        diffValor,
        match: diffQtd === 0 && Math.abs(diffValor) < 0.01,
      };
    });
  }, [resumida, commitResult, parsedItems]);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Importar Inscrições" size="3xl">
      {step === 'upload' && (
        <div className="space-y-4">
          <div className="rounded-lg border border-dashed border-gray-300 p-4">
            <label className="block">
              <span className="text-sm font-medium text-gray-700">
                Planilha detalhada (obrigatório)
                <span className="ml-1 text-xs text-gray-500">— uma linha por pessoa</span>
              </span>
              <div className="mt-2 flex items-center gap-2">
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => setDetalhadoFile(e.target.files?.[0] ?? null)}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:rounded-md file:border-0 file:bg-primary-50 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-primary-700 hover:file:bg-primary-100"
                />
                {detalhadoFile && (
                  <button onClick={() => setDetalhadoFile(null)} className="text-gray-400 hover:text-gray-600">
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </label>
          </div>

          <div className="rounded-lg border border-dashed border-gray-300 p-4">
            <label className="block">
              <span className="text-sm font-medium text-gray-700">
                Planilha resumida (opcional)
                <span className="ml-1 text-xs text-gray-500">— para conferir totais por clube/lote</span>
              </span>
              <div className="mt-2 flex items-center gap-2">
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => setResumidaFile(e.target.files?.[0] ?? null)}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:rounded-md file:border-0 file:bg-gray-50 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-gray-700 hover:file:bg-gray-100"
                />
                {resumidaFile && (
                  <button onClick={() => setResumidaFile(null)} className="text-gray-400 hover:text-gray-600">
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Status de pagamento padrão
            </label>
            <select
              value={statusPadrao}
              onChange={(e) => setStatusPadrao(e.target.value as 'PAGO' | 'PENDENTE')}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              <option value="PAGO">PAGO — gera conta a receber + lançamento no livro caixa</option>
              <option value="PENDENTE">PENDENTE — cria só a inscrição</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Linhas com valor zerado serão tratadas como cortesia (sem efeito financeiro).
            </p>
          </div>

          {parseErrors.length > 0 && (
            <div className="rounded-lg bg-red-50 p-3">
              <div className="flex items-start gap-2">
                <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-500" />
                <div className="space-y-1 text-sm text-red-800">
                  {parseErrors.map((e, i) => (
                    <p key={i}>{e}</p>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={handleClose}>
              Cancelar
            </Button>
            <Button onClick={handleAnalisar} disabled={!detalhadoFile}>
              <Upload className="h-4 w-4" />
              Analisar
            </Button>
          </div>
        </div>
      )}

      {step === 'parsing' && (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
            <p className="mt-3 text-sm text-gray-600">Lendo planilha…</p>
          </div>
        </div>
      )}

      {step === 'preview' && (
        <div className="space-y-4">
          {previewMutation.isPending && (
            <div className="flex items-center justify-center py-8">
              <div className="h-6 w-6 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
            </div>
          )}

          {previewMutation.data && (
            <>
              <div className="rounded-lg bg-blue-50 p-3 text-sm text-blue-800">
                <FileSpreadsheet className="mr-2 inline h-4 w-4" />
                {parseInfo} — {previewMutation.data.novas.length} novas, {previewMutation.data.atualizacoes.length} atualizações,{' '}
                {previewMutation.data.inalteradas.length} inalteradas
                {previewMutation.data.ambiguas.length > 0 && `, ${previewMutation.data.ambiguas.length} ambíguas`}
                {previewMutation.data.ausentes.length > 0 && `, ${previewMutation.data.ausentes.length} ausentes da planilha`}
              </div>

              {previewMutation.data.excedeLimite && (
                <div className="rounded-lg bg-red-50 p-3 text-sm text-red-800">
                  <AlertCircle className="mr-2 inline h-4 w-4" />
                  Excede o limite de participantes do evento. Restam {previewMutation.data.vagasRestantes} vagas, e você está
                  tentando criar {selecaoNovas.size} inscrições.
                </div>
              )}

              <div className="flex gap-2 border-b border-gray-200">
                {(['novas', 'atualizacoes', 'inalteradas', 'ambiguas', 'ausentes'] as const).map((tab) => {
                  const counts = {
                    novas: previewMutation.data.novas.length,
                    atualizacoes: previewMutation.data.atualizacoes.length,
                    inalteradas: previewMutation.data.inalteradas.length,
                    ambiguas: previewMutation.data.ambiguas.length,
                    ausentes: previewMutation.data.ausentes.length,
                  };
                  if (tab === 'ambiguas' && counts.ambiguas === 0) return null;
                  if (tab === 'ausentes' && counts.ausentes === 0) return null;
                  const labels: Record<typeof tab, string> = {
                    novas: 'novas',
                    atualizacoes: 'Atualizações',
                    inalteradas: 'inalteradas',
                    ambiguas: 'ambiguas',
                    ausentes: 'Ausentes',
                  };
                  return (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={`border-b-2 px-3 py-2 text-sm font-medium capitalize ${
                        activeTab === tab
                          ? 'border-primary-600 text-primary-700'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      {labels[tab]} ({counts[tab]})
                    </button>
                  );
                })}
              </div>

              <div className="max-h-[50vh] overflow-y-auto">
                {activeTab === 'novas' && (
                  <PreviewTable
                    rows={previewMutation.data.novas}
                    selecao={selecaoNovas}
                    onToggle={(i) => {
                      const s = new Set(selecaoNovas);
                      if (s.has(i)) s.delete(i);
                      else s.add(i);
                      setSelecaoNovas(s);
                    }}
                    onToggleAll={(all) => {
                      setSelecaoNovas(all ? new Set(previewMutation.data.novas.map((_, i) => i)) : new Set());
                    }}
                    diffMode={false}
                  />
                )}
                {activeTab === 'atualizacoes' && (
                  <UpdatesTable
                    rows={previewMutation.data.atualizacoes}
                    selecao={selecaoAtualizacoes}
                    onToggle={(i) => {
                      const s = new Set(selecaoAtualizacoes);
                      if (s.has(i)) s.delete(i);
                      else s.add(i);
                      setSelecaoAtualizacoes(s);
                    }}
                    onToggleAll={(all) => {
                      setSelecaoAtualizacoes(all ? new Set(previewMutation.data.atualizacoes.map((_, i) => i)) : new Set());
                    }}
                  />
                )}
                {activeTab === 'inalteradas' && (
                  <PreviewTable rows={previewMutation.data.inalteradas} selecao={null} onToggle={() => {}} onToggleAll={() => {}} diffMode={false} />
                )}
                {activeTab === 'ambiguas' && (
                  <PreviewTable
                    rows={previewMutation.data.ambiguas}
                    selecao={selecaoAmbiguas}
                    onToggle={(i) => {
                      const s = new Set(selecaoAmbiguas);
                      if (s.has(i)) s.delete(i);
                      else s.add(i);
                      setSelecaoAmbiguas(s);
                    }}
                    onToggleAll={(all) => {
                      setSelecaoAmbiguas(all ? new Set(previewMutation.data.ambiguas.map((_, i) => i)) : new Set());
                    }}
                    diffMode={false}
                  />
                )}
                {activeTab === 'ausentes' && (
                  <>
                    <div className="mb-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
                      <AlertCircle className="mr-2 inline h-4 w-4" />
                      Estas inscrições estão no sistema mas não aparecem na planilha. As selecionadas serão
                      <strong> removidas</strong> (junto do lançamento no livro caixa) ao importar.
                    </div>
                    <PreviewTable
                      rows={previewMutation.data.ausentes}
                      selecao={selecaoAusentes}
                      onToggle={(i) => {
                        const s = new Set(selecaoAusentes);
                        if (s.has(i)) s.delete(i);
                        else s.add(i);
                        setSelecaoAusentes(s);
                      }}
                      onToggleAll={(all) => {
                        setSelecaoAusentes(all ? new Set(previewMutation.data.ausentes.map((_, i) => i)) : new Set());
                      }}
                      diffMode={false}
                    />
                  </>
                )}
              </div>

              <div className="flex justify-between gap-2 pt-2">
                <Button variant="outline" onClick={() => setStep('upload')}>
                  Voltar
                </Button>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={handleClose}>
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleCommit}
                    isLoading={committing}
                    disabled={committing || previewMutation.data.excedeLimite || (selecaoNovas.size + selecaoAtualizacoes.size + selecaoAmbiguas.size + selecaoAusentes.size === 0)}
                  >
                    {(() => {
                      const partes: string[] = [];
                      const criarCount = selecaoNovas.size + selecaoAmbiguas.size;
                      if (criarCount > 0) partes.push(`${criarCount} novas`);
                      if (selecaoAtualizacoes.size > 0) partes.push(`${selecaoAtualizacoes.size} atualizações`);
                      if (selecaoAusentes.size > 0) partes.push(`${selecaoAusentes.size} a remover`);
                      return partes.length > 0 ? `Importar (${partes.join(', ')})` : 'Importar';
                    })()}
                  </Button>
                </div>
              </div>
            </>
          )}

          {previewMutation.error && (
            <div className="rounded-lg bg-red-50 p-3 text-sm text-red-800">
              Erro ao carregar preview: {previewMutation.error.message}
            </div>
          )}
        </div>
      )}

      {step === 'result' && commitResult && (
        <div className="space-y-4">
          <div className="flex items-center gap-3 rounded-lg bg-green-50 p-4">
            <CheckCircle2 className="h-6 w-6 text-green-600" />
            <div>
              <p className="font-medium text-green-900">Importação concluída</p>
              <p className="text-sm text-green-800">
                {commitResult.criadas} criadas, {commitResult.atualizadas} atualizadas
                {commitResult.removidas > 0 && `, ${commitResult.removidas} removidas`}
                {commitResult.ignoradas > 0 && `, ${commitResult.ignoradas} ignoradas (já existiam)`}
              </p>
            </div>
          </div>

          {reconciliacao && reconciliacao.length > 0 && (
            <div>
              <p className="mb-2 text-sm font-medium text-gray-700">Conferência com planilha resumida</p>
              <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-700">Clube</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-700">Lote</th>
                      <th className="px-3 py-2 text-right font-medium text-gray-700">Qtd planilha</th>
                      <th className="px-3 py-2 text-right font-medium text-gray-700">Qtd importada</th>
                      <th className="px-3 py-2 text-right font-medium text-gray-700">Δ Valor</th>
                      <th className="px-3 py-2 text-center font-medium text-gray-700"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {reconciliacao.map((r, i) => (
                      <tr key={i} className={r.match ? '' : 'bg-yellow-50'}>
                        <td className="px-3 py-2">{r.clube}</td>
                        <td className="px-3 py-2 text-gray-600">{r.lote}</td>
                        <td className="px-3 py-2 text-right">{r.qtdDeclarada}</td>
                        <td className="px-3 py-2 text-right">{r.qtdImportada}</td>
                        <td className="px-3 py-2 text-right">{formatCurrency(r.diffValor)}</td>
                        <td className="px-3 py-2 text-center">
                          {r.match ? (
                            <CheckCircle2 className="inline h-4 w-4 text-green-600" />
                          ) : (
                            <AlertCircle className="inline h-4 w-4 text-yellow-600" />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="flex justify-end pt-2">
            <Button onClick={handleClose}>Fechar</Button>
          </div>
        </div>
      )}
    </Modal>
  );

  function PreviewTable({
    rows,
    selecao,
    onToggle,
    onToggleAll,
  }: {
    rows: ParsedItem[];
    selecao: Set<number> | null;
    onToggle: (i: number) => void;
    onToggleAll: (all: boolean) => void;
    diffMode: boolean;
  }) {
    const allChecked = selecao !== null && rows.length > 0 && selecao.size === rows.length;
    return (
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50">
          <tr>
            {selecao !== null && (
              <th className="px-2 py-2">
                <input
                  type="checkbox"
                  checked={allChecked}
                  onChange={(e) => onToggleAll(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300"
                />
              </th>
            )}
            <th className="px-3 py-2 text-left font-medium text-gray-700">Nome</th>
            <th className="px-3 py-2 text-left font-medium text-gray-700">Clube</th>
            <th className="px-3 py-2 text-left font-medium text-gray-700">Lote</th>
            <th className="px-3 py-2 text-right font-medium text-gray-700">Valor</th>
            <th className="px-3 py-2 text-left font-medium text-gray-700">Restrição</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.length === 0 && (
            <tr>
              <td colSpan={selecao !== null ? 6 : 5} className="px-3 py-6 text-center text-gray-400">
                Nenhuma linha
              </td>
            </tr>
          )}
          {rows.map((row, i) => (
            <tr key={i} className={selecao?.has(i) ? 'bg-primary-50/40' : ''}>
              {selecao !== null && (
                <td className="px-2 py-1.5">
                  <input
                    type="checkbox"
                    checked={selecao.has(i)}
                    onChange={() => onToggle(i)}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                </td>
              )}
              <td className="px-3 py-1.5 font-medium">{row.nome}</td>
              <td className="px-3 py-1.5 text-gray-600">{row.clube ?? '-'}</td>
              <td className="px-3 py-1.5 text-gray-600">{row.lote ?? '-'}</td>
              <td className="px-3 py-1.5 text-right">{formatCurrency(row.valor)}</td>
              <td className="px-3 py-1.5 text-gray-600">{row.restricaoAlimentar ?? '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  function UpdatesTable({
    rows,
    selecao,
    onToggle,
    onToggleAll,
  }: {
    rows: Array<{ existingId: string; existingNome: string; novo: ParsedItem; diff: string[] }>;
    selecao: Set<number>;
    onToggle: (i: number) => void;
    onToggleAll: (all: boolean) => void;
  }) {
    const allChecked = rows.length > 0 && selecao.size === rows.length;
    return (
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-2 py-2">
              <input
                type="checkbox"
                checked={allChecked}
                onChange={(e) => onToggleAll(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300"
              />
            </th>
            <th className="px-3 py-2 text-left font-medium text-gray-700">Nome</th>
            <th className="px-3 py-2 text-left font-medium text-gray-700">Campos alterados</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.length === 0 && (
            <tr>
              <td colSpan={3} className="px-3 py-6 text-center text-gray-400">
                Nenhuma atualização detectada
              </td>
            </tr>
          )}
          {rows.map((row, i) => (
            <tr key={i} className={selecao.has(i) ? 'bg-primary-50/40' : ''}>
              <td className="px-2 py-1.5">
                <input
                  type="checkbox"
                  checked={selecao.has(i)}
                  onChange={() => onToggle(i)}
                  className="h-4 w-4 rounded border-gray-300"
                />
              </td>
              <td className="px-3 py-1.5 font-medium">{row.existingNome}</td>
              <td className="px-3 py-1.5">
                <div className="flex flex-wrap gap-1">
                  {row.diff.map((f) => (
                    <span key={f} className="inline-flex rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-800">
                      {DIFF_LABELS[f] ?? f}
                    </span>
                  ))}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
}
